import {
  Badge,
  Button,
  HStack,
  IconButton,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react";
import type { Control, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";
import type { AudienceData } from "@common/interfaces/KYDMarketing";
import trashIcon from "../assets/trash-icon.svg";
import {
  audienceTargetingKey,
  handleAudienceSelection,
} from "./AudienceList";

/** Visual layout for this panel — kept in-file. */
const selectedAudiencePanelStyles = {
  fontFamily: `'Work Sans', sans-serif`,
  titleColor: "#1a202c",
  titleFontSize: "16px",
  titleFontWeight: 600,
  titleLineHeight: 1.3,
  subtitleColor: "rgba(26, 32, 44, 0.6)",
  subtitleFontSize: "14px",
  subtitleFontWeight: 500,
  subtitleLineHeight: 1.4,
  sectionGapPx: 12,
  cardBg: "white",
  cardBorder: "2px solid #1d74ff",
  cardRadius: "8px",
  cardPadding: "12px",
  cardGapPx: 24,
  cardShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.05)",
  tagBg: "#dfeafd",
  tagColor: "rgba(26, 32, 44, 0.8)",
  tagFontSize: "12px",
  tagFontWeight: 500,
  tagLineHeight: 1.4,
  tagRadius: "4px",
  tagPx: "4px",
  tagPy: "2px",
  nameColor: "#2c333e",
  nameFontSize: "16px",
  nameFontWeight: 500,
  nameLineHeight: 1.4,
  labelStackGapPx: 6,
  actionBorder: "1px solid #e2e2e2",
  actionBg: "white",
  actionRadius: "10px",
  actionPx: "12px",
  actionPy: "10px",
  actionShadowIcon: "0px 2px 6px 0px rgba(0, 0, 0, 0.05)",
  actionShadowEdit: "0px 2px 3px rgba(0, 0, 0, 0.05)",
  editTextColor: "#1a202c",
  editFontSize: "13px",
  editFontWeight: 500,
  editLineHeight: "19.2px",
  trashIconW: "18px",
  trashIconH: "20px",
} as const;

const s = selectedAudiencePanelStyles;

function metaAudienceBadgeLabel(audience: AudienceData): string {
  switch (audience.audience_type) {
    case "meta":
    case "page_engagers":
    case "page_lookalike":
      return "META";
    case "kyd":
      return "KYD";
    case "csv":
      return "CSV";
    default:
      return "AUDIENCE";
  }
}

type InterestRow = { key: string; value: string };

type SelectedAudiencesPanelProps = {
  audiences: AudienceData[];
  control: Control<FieldValues>;
  fieldName: string;
  interests?: InterestRow[];
  onEditInterests?: () => void;
  /**
   * When true, interests are not rendered in this panel (guided Smart-off flow: the
   * `GuidedTargetingFields` "Interests" section is the only place they appear, avoiding audience-style cards here).
   */
  hideInterestsSummary?: boolean;
};

function formatInterestsSummary(interests: InterestRow[]): string {
  if (interests.length === 0) return "";
  const labels = interests.map((i) => i.value);
  const shown = labels.slice(0, 4);
  const tail =
    labels.length > 4 ? `${shown.join(", ")} and more` : shown.join(", ");
  return `Interests: ${tail}`;
}

function TrashIconButton({
  ariaLabel,
  onClick,
}: {
  ariaLabel: string;
  onClick: () => void;
}) {
  return (
    <IconButton
      aria-label={ariaLabel}
      icon={
        <Image
          src={trashIcon}
          alt=""
          w={s.trashIconW}
          h={s.trashIconH}
          objectFit="contain"
        />
      }
      variant="outline"
      borderWidth="1px"
      borderColor="transparent"
      sx={{
        borderColor: "#e2e2e2",
        bg: s.actionBg,
        borderRadius: s.actionRadius,
        px: s.actionPx,
        py: s.actionPy,
        boxShadow: s.actionShadowIcon,
        minW: "auto",
        h: "auto",
        fontSize: 0,
      }}
      _focusVisible={{
        boxShadow: `${s.actionShadowIcon}, 0 0 0 2px #1d74ff`,
      }}
      onClick={onClick}
    />
  );
}

export default function SelectedAudiencesPanel({
  audiences,
  control,
  fieldName,
  interests = [],
  onEditInterests,
  hideInterestsSummary = false,
}: SelectedAudiencesPanelProps) {
  const hasInterests = interests.length > 0;
  const showInterestsBlock = hasInterests && !hideInterestsSummary;
  const showEmpty =
    audiences.length === 0 && (!hasInterests || hideInterestsSummary);

  return (
    <VStack
      align="stretch"
      spacing={`${s.sectionGapPx}px`}
      w="full"
      fontFamily={s.fontFamily}
    >
      <VStack align="flex-start" spacing={`${s.labelStackGapPx}px`} w="full">
        <Text
          fontWeight={s.titleFontWeight}
          fontSize={s.titleFontSize}
          color={s.titleColor}
          lineHeight={s.titleLineHeight}
        >
          Selected audiences
        </Text>
        <Text
          fontSize={s.subtitleFontSize}
          fontWeight={s.subtitleFontWeight}
          color={s.subtitleColor}
          lineHeight={s.subtitleLineHeight}
        >
          Create a new audience or reuse one from past campaigns.
        </Text>
      </VStack>

      {showEmpty ? (
        <Text
          fontSize={s.subtitleFontSize}
          fontWeight={s.subtitleFontWeight}
          color={s.subtitleColor}
          lineHeight={s.subtitleLineHeight}
        >
          No audiences selected yet. Search below to add Meta audiences, or
          create one using the options above.
        </Text>
      ) : null}

      <Controller
        control={control}
        name={fieldName}
        render={({ field }) => (
          <VStack
            align="stretch"
            spacing={`${s.sectionGapPx}px`}
            w="full"
          >
            {audiences.map((audience) => {
              const rowKey =
                audienceTargetingKey(audience) ||
                audience.external_id ||
                audience.id ||
                audience.name;
              return (
                <HStack
                  key={String(rowKey)}
                  align="center"
                  spacing={`${s.cardGapPx}px`}
                  w="full"
                  p={s.cardPadding}
                  bg={s.cardBg}
                  border={s.cardBorder}
                  borderRadius={s.cardRadius}
                  boxShadow={s.cardShadow}
                  overflow="hidden"
                >
                  <VStack
                    align="flex-start"
                    spacing={`${s.labelStackGapPx}px`}
                    flex={1}
                    minW={0}
                  >
                    <Badge
                      bg={s.tagBg}
                      color={s.tagColor}
                      fontWeight={s.tagFontWeight}
                      fontSize={s.tagFontSize}
                      lineHeight={s.tagLineHeight}
                      px={s.tagPx}
                      py={s.tagPy}
                      borderRadius={s.tagRadius}
                      textTransform="none"
                      fontFamily={s.fontFamily}
                    >
                      {metaAudienceBadgeLabel(audience)}
                    </Badge>
                    <Text
                      fontSize={s.nameFontSize}
                      fontWeight={s.nameFontWeight}
                      color={s.nameColor}
                      lineHeight={s.nameLineHeight}
                      noOfLines={2}
                      wordBreak="break-word"
                      fontFamily={s.fontFamily}
                    >
                      {audience.name}
                    </Text>
                  </VStack>
                  <TrashIconButton
                    ariaLabel={`Remove ${audience.name ?? "audience"}`}
                    onClick={() =>
                      handleAudienceSelection(
                        audience,
                        field.value || [],
                        field.onChange,
                        false
                      )
                    }
                  />
                </HStack>
              );
            })}
          </VStack>
        )}
      />

      {showInterestsBlock ? (
        <HStack
          align="center"
          spacing={`${s.cardGapPx}px`}
          w="full"
          p={s.cardPadding}
          bg={s.cardBg}
          border={s.cardBorder}
          borderRadius={s.cardRadius}
          boxShadow={s.cardShadow}
          overflow="hidden"
        >
          <VStack
            align="flex-start"
            spacing={`${s.labelStackGapPx}px`}
            flex={1}
            minW={0}
          >
            <Badge
              bg={s.tagBg}
              color={s.tagColor}
              fontWeight={s.tagFontWeight}
              fontSize={s.tagFontSize}
              lineHeight={s.tagLineHeight}
              px={s.tagPx}
              py={s.tagPy}
              borderRadius={s.tagRadius}
              textTransform="none"
              fontFamily={s.fontFamily}
            >
              META • INTERESTS
            </Badge>
            <Text
              fontSize={s.nameFontSize}
              fontWeight={s.nameFontWeight}
              color={s.nameColor}
              lineHeight={s.nameLineHeight}
              noOfLines={2}
              overflow="hidden"
              textOverflow="ellipsis"
              title={interests.map((i) => i.value).join(", ")}
              fontFamily={s.fontFamily}
            >
              {formatInterestsSummary(interests)}
            </Text>
          </VStack>
          {onEditInterests ? (
            <Button
              variant="outline"
              borderWidth="1px"
              borderColor="transparent"
              sx={{
                borderColor: "#e2e2e2",
                bg: s.actionBg,
                borderRadius: s.actionRadius,
                px: s.actionPx,
                py: s.actionPy,
                boxShadow: s.actionShadowEdit,
                fontFamily: s.fontFamily,
                fontSize: s.editFontSize,
                fontWeight: s.editFontWeight,
                color: s.editTextColor,
                lineHeight: s.editLineHeight,
                h: "auto",
                minH: 0,
              }}
              _focusVisible={{
                boxShadow: `${s.actionShadowEdit}, 0 0 0 2px #1d74ff`,
              }}
              onClick={onEditInterests}
            >
              Edit
            </Button>
          ) : null}
          <Controller
            control={control}
            name="interests"
            render={({ field: interestsField }) => (
              <TrashIconButton
                ariaLabel="Clear interests"
                onClick={() => interestsField.onChange([])}
              />
            )}
          />
        </HStack>
      ) : null}
    </VStack>
  );
}
